import { useEffect, useState } from "react";
import { Link, useParams, useRouteLoaderData } from "react-router";
import type { JobStatus } from "@edm/contracts";
import { KeyValueSkeleton, Loading } from "../../components/skeletons.jsx";
import { ErrorState, LoadingState } from "../../components/states.jsx";
import { useI18n } from "../../i18n/index.js";
import { api, isApiError } from "../../lib/api.js";
import { AlertIcon, ArrowLeftIcon, Callout, Card, CheckIcon, KeyValue, PageHeader, Skeleton, SpinnerIcon, StatusBadge, toneFor } from "../../ui/index.js";
import type { workspaceLayoutLoader } from "./layout.jsx";

const TERMINAL = new Set(["succeeded", "failed", "dead"]);

function BackToPulse() {
  const { workspaceSlug } = useParams();
  const { t } = useI18n();
  return (
    <Link to={`/w/${workspaceSlug}`} className="dt-inline dt-gap-1 dt-align-center" style={{ color: "inherit" }}>
      <ArrowLeftIcon /> {t("operation.back")}
    </Link>
  );
}

/** Until the first answer: the status card and the details, without their values. */
function StatusSkeleton() {
  const { t } = useI18n();
  return (
    <Loading>
      <Card>
        <div className="dt-op-hero">
          <Skeleton shape="circle" width={44} height={44} />
          <div className="dt-op-hero__text">
            <h2>
              <Skeleton width={220} />
            </h2>
            <span className="dt-inline dt-gap-2 dt-align-center">
              <Skeleton shape="badge" width={80} />
              <Skeleton width={120} />
            </span>
          </div>
        </div>
      </Card>
      <Card title={t("operation.details")}>
        <KeyValueSkeleton keys={[t("operation.kv.type"), t("operation.kv.attempts"), t("operation.kv.state")]} />
      </Card>
    </Loading>
  );
}

export function OperationSkeleton() {
  const { t } = useI18n();
  return (
    <>
      <PageHeader eyebrow={<BackToPulse />} title={t("operation.title")} />
      <StatusSkeleton />
    </>
  );
}

// Polls a deferred operation until it reaches a terminal state.
export function OperationRoute() {
  const { id = "" } = useParams();
  const { workspace } = useRouteLoaderData("workspace") as ReturnType<typeof workspaceLayoutLoader>;
  const { t } = useI18n();
  const [status, setStatus] = useState<JobStatus | null>(null);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      try {
        const s = await api.workspace(workspace.id).getOperation(id, ctrl.signal);
        setStatus(s);
        if (!TERMINAL.has(s.status)) timer = setTimeout(tick, 1500);
      } catch (err) {
        if (ctrl.signal.aborted) return;
        setError(isApiError(err) ? { code: err.code, message: err.message } : { code: "unknown", message: String(err) });
      }
    };
    void tick();
    return () => {
      ctrl.abort();
      if (timer) clearTimeout(timer);
    };
  }, [id, workspace.id]);

  const back = <BackToPulse />;

  if (error) {
    return (
      <>
        <PageHeader eyebrow={back} title={t("operation.title")} />
        <ErrorState code={error.code} message={error.message} />
      </>
    );
  }
  if (!status) return <OperationSkeleton />;

  const tone = toneFor(status.status);
  const done = TERMINAL.has(status.status);
  return (
    <>
      <PageHeader eyebrow={back} title={t("operation.title")} />
      <Card>
        <div className="dt-op-hero">
          <span className="dt-op-hero__status" data-tone={tone}>
            {tone === "success" ? <CheckIcon size={20} /> : tone === "danger" ? <AlertIcon size={20} /> : <SpinnerIcon size={20} />}
          </span>
          <div className="dt-op-hero__text">
            <h2>
              <code>{status.id}</code>
            </h2>
            <span className="dt-inline dt-gap-2 dt-align-center dt-muted">
              <StatusBadge status={status.status} />
              <span>{status.type}</span>
            </span>
          </div>
        </div>
      </Card>
      <Card title={t("operation.details")}>
        <KeyValue
          items={[
            { key: t("operation.kv.type"), value: <code>{status.type}</code> },
            { key: t("operation.kv.attempts"), value: t("operation.kv.attemptsValue", { attempts: status.attempts, max: status.maxAttempts }) },
            { key: t("operation.kv.state"), value: done ? t("operation.state.terminal") : t("operation.state.inProgress") },
          ]}
        />
      </Card>
      {status.lastError && (
        <Callout tone="danger" title={t("operation.lastError")}>
          <code style={{ background: "none", padding: 0 }}>{status.lastError}</code>
        </Callout>
      )}
      {!done && <LoadingState label={t("operation.waiting")} />}
    </>
  );
}
